import type { ReactNode } from "react";
import { useCallback, useDeferredValue, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, DropdownButton } from "@similarweb/ui-components";

import { ScrollDismissBoundary } from "../../scroll-dismiss/ScrollDismissBoundary";
import { StyledBaseDropdownButtonWrap } from "../../styles";
import type { IGenericDropDownItem } from "../types";

type ItemId = string | number;

type SelectComboboxProps<TItem extends IGenericDropDownItem<ItemId>> = {
    items: readonly TItem[];
    isItemSelected: (item: TItem) => boolean;
    onSelect: (item: TItem) => void;
    // "multiple" (default): picking stays open. "single": closes the popover on pick. Either way the
    // consumer owns the selection model in onSelect and the checkbox/indicator in renderOption.
    selectionMode?: "single" | "multiple";
    // Opens the popover on mount (uncontrolled initial open state), mirroring the legacy Dropdown
    // `isOpenedByDefault`/`defaultOpen`.
    defaultOpen?: boolean;
    // Owns the FULL option content (e.g. the legacy MultiSelect item with its own checkbox/chevron).
    renderOption: (item: TItem, isSelected: boolean) => ReactNode;
    // Emits a Combobox.Separator above the matching option (e.g. between permitted and restricted
    // countries).
    renderSeparatorBefore?: (item: TItem) => boolean;
    // Custom trigger. MUST return a single ref-forwarding DOM element (e.g. a styled.div):
    // Combobox.Trigger merges its press handlers + ref onto this child via PopoverTrigger `asChild`,
    // so a custom-component wrapper that drops the ref leaves the trigger inert.
    renderButton?: () => ReactNode;
    buttonText?: string;
    // Explicit popover width in px; when omitted the popup is content-sized and can be narrower than the trigger.
    width?: number;
    // Required when the combobox is rendered inside a modal so the popover stacks above the modal layer.
    isTopLayer?: boolean;
    hasSearch?: boolean;
    searchPlaceHolder?: string;
    // Controlled search: when `onSearchChange` is provided, the consumer owns the search term (and is
    // expected to pre-filter `items`, typically with `filterItem={() => true}`). Otherwise search is
    // internal state that filters `items` via `filterItem`.
    searchValue?: string;
    onSearchChange?: (searchTerm: string) => void;
    // Custom search predicate; defaults to a case-insensitive match on the item's text.
    filterItem?: (item: TItem, searchTerm: string) => boolean;
    disabled?: boolean;
    dataAutomation?: string;
    emptyStateText?: string;
};

/**
 * Non-virtualized item-based select Combobox for option lists whose items own their own rendering
 * (flag/region rows, checkboxes, counts, expandable categories) via `renderOption`. Supports single
 * and multi selection via `selectionMode` — selection itself is a per-item callback (`onSelect`), so
 * the consumer keeps its own selection model. Options mount only while the dropdown is open (Combobox
 * unmounts its content when closed), so a closed dropdown costs ~no DOM — the win over the legacy
 * Dropdown, which renders its whole option list eagerly.
 *
 * For value-based form selects (primitive option values + formatValue) use FormSelectCombobox instead.
 */
const SelectCombobox = <TItem extends IGenericDropDownItem<ItemId>>({
    items,
    isItemSelected,
    onSelect,
    selectionMode = "multiple",
    defaultOpen = false,
    renderOption,
    renderSeparatorBefore,
    renderButton,
    buttonText,
    width,
    isTopLayer = false,
    hasSearch = false,
    searchPlaceHolder,
    searchValue,
    onSearchChange,
    filterItem,
    disabled = false,
    dataAutomation,
    emptyStateText,
}: SelectComboboxProps<TItem>) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(defaultOpen && !disabled);
    const [internalSearchTerm, setInternalSearchTerm] = useState("");

    if (disabled && isOpen) {
        setIsOpen(false);
        if (!onSearchChange) {
            setInternalSearchTerm("");
        }
    }

    const searchTerm = onSearchChange ? (searchValue ?? "") : internalSearchTerm;
    const deferredSearchTerm = useDeferredValue(searchTerm);

    const setSearchTerm = useCallback(
        (term: string) => {
            if (onSearchChange) {
                onSearchChange(term);
            } else {
                setInternalSearchTerm(term);
            }
        },
        [onSearchChange],
    );

    const filteredItems = useMemo(() => {
        const term = deferredSearchTerm.trim().toLowerCase();
        if (!term) {
            return items;
        }

        const matches =
            filterItem ?? ((item: TItem, value: string) => Boolean(item.text?.toLowerCase().includes(value)));

        return items.filter((item) => matches(item, term));
    }, [items, deferredSearchTerm, filterItem]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (disabled && open) {
                return;
            }

            setIsOpen(open);
            // Reset only internal search on close; controlled search is owned by the consumer.
            if (!open && !onSearchChange) {
                setInternalSearchTerm("");
            }
        },
        [disabled, onSearchChange],
    );

    const handleSelect = useCallback(
        (item: TItem) => {
            onSelect(item);
            if (selectionMode === "single") {
                setIsOpen(false);
            }
        },
        [onSelect, selectionMode],
    );

    return (
        <ScrollDismissBoundary
            isOpen={isOpen}
            onClose={() => handleOpenChange(false)}
        >
            <Combobox
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                placement="bottom-start"
                width={width ? `${width}px` : undefined}
                data-automation={dataAutomation}
                {...(isTopLayer ? { "data-react-aria-top-layer": "true" } : {})}
            >
                <Combobox.Trigger>
                    {renderButton ? (
                        renderButton()
                    ) : (
                        <StyledBaseDropdownButtonWrap disabled={disabled}>
                            <DropdownButton disabled={disabled}>{buttonText}</DropdownButton>
                        </StyledBaseDropdownButtonWrap>
                    )}
                </Combobox.Trigger>

                {hasSearch ? (
                    <Combobox.SearchInput
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder={searchPlaceHolder}
                    />
                ) : null}

                <Combobox.Content
                    emptyState={
                        <Combobox.OptionLabel>
                            {emptyStateText ?? translate("dropdown.no.results")}
                        </Combobox.OptionLabel>
                    }
                >
                    {filteredItems.flatMap((item) => {
                        const option = (
                            <Combobox.Option
                                key={String(item.id)}
                                id={String(item.id)}
                                textValue={item.text ?? ""}
                                onClick={() => handleSelect(item)}
                            >
                                {renderOption(item, isItemSelected(item))}
                            </Combobox.Option>
                        );

                        // Flat array instead of a fragment — react-aria collections don't support
                        // fragment children.
                        return renderSeparatorBefore?.(item)
                            ? [<Combobox.Separator key={`separator-${String(item.id)}`} />, option]
                            : [option];
                    })}
                </Combobox.Content>
            </Combobox>
        </ScrollDismissBoundary>
    );
};

export default SelectCombobox;
